/**
 * OVERRIDE DaySchedule.tsx
 * FILE: @plone-collective/volto-techevent/src/components/Schedule/DaySchedule.tsx
 * DATE: 2026-09-11
 *
 * CHANGES vs. upstream:
 *
 * 1. data-start / data-end attributes on every .timeslot element
 *    The Schedule.jsx override uses them to find the DOM node of the
 *    next upcoming slot for the auto-scroll-to-now feature. Purely
 *    additive — no visual or behavioral change.
 *
 * 2. Import style
 *    SessionTile and SlotTile are imported via absolute package paths
 *    instead of relative ones, because this file lives in the project's
 *    customizations tree where those sibling files do not exist.
 *
 * 3. Plenary rendering for room-all slots with an assigned room
 *    The backend gives Keynotes (and room-less slots) the gridColumn
 *    `room-1 / room-all`. A room-all slot WITH an assigned room now
 *    renders only in that room's column, and the other room columns get
 *    a grayed-out `.timeslot-blocked` overlay for the same time span —
 *    signalling "nothing happens in the other rooms during this plenary"
 *    (planRoomAllSlot in ./scheduleUtils). Room-less slots (breaks,
 *    registration) keep the full span.
 *
 * (The ICS download button lives in the SlotDate.jsx override, inside
 * `.slotDate .wrapper` after the end time.)
 *
 * Everything else is identical to the upstream file.
 */

import React from 'react';
import { formatHour } from '@plone-collective/volto-techevent/helpers/date';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import SessionTile from '@plone-collective/volto-techevent/components/Schedule/SessionTile';
import SlotTile from '@plone-collective/volto-techevent/components/Schedule/SlotTile';
import lunchSVG from '@plone-collective/volto-techevent/icons/lunch.svg';
import microphoneSVG from '@plone-collective/volto-techevent/icons/microphone.svg';
import trainingSVG from '@plone-collective/volto-techevent/icons/training.svg';
import coffeeSVG from '@plone-collective/volto-techevent/icons/coffee.svg';
import beerSVG from '@plone-collective/volto-techevent/icons/beer.svg';
import meetingSVG from '@plone-collective/volto-techevent/icons/meeting.svg';
import keynoteSVG from '@plone/volto/icons/star.svg';
import registrationSVG from '@plone/volto/icons/finger-print.svg';
import roomSVG from '@plone/volto/icons/map.svg';
import lightingTalksSVG from '@plone/volto/icons/fast-forward.svg';
import cameraSVG from '@plone/volto/icons/camera.svg';
import { planRoomAllSlot } from './scheduleUtils';

const iconDict = {
  registration: registrationSVG,
  Slot: coffeeSVG,
  Break: coffeeSVG,
  photo: cameraSVG,
  lunch: lunchSVG,
  party: beerSVG,
  Meeting: meetingSVG,
  OpenSpace: meetingSVG,
  LightningTalks: lightingTalksSVG,
  Training: trainingSVG,
  Talk: microphoneSVG,
  Keynote: keynoteSVG,
};

function nextTimeSlot(t) {
  // Get the next time slot (15 min increments)
  let h = parseInt(t.slice(0, 2), 10);
  let m = parseInt(t.slice(2, 4), 10) + 15;
  if (m >= 60) {
    h += 1;
    m -= 60;
  }
  // Pad hours and minutes with leading zeros
  return h.toString().padStart(2, '0') + m.toString().padStart(2, '0');
}

function getGridRowsAndColumns(slots) {
  // Get all unique grid rows and columns
  const gridRows = new Set();
  const gridColumnsSet = new Set();
  slots.forEach(({ gridRow = '', gridColumn = '' }) => {
    gridRow.split('/').forEach((label) => gridRows.add(label.trim()));
    gridColumn.split('/').forEach((label) => gridColumnsSet.add(label.trim()));
  });
  // Sort gridColumns
  const gridColumns = Array.from(gridColumnsSet)
    .filter((label) => /^room-\d+$/.test(label))
    .sort(
      (a, b) => parseInt(a.split('-')[1], 10) - parseInt(b.split('-')[1], 10),
    );
  return { gridRows, gridColumns };
}

function buildContinuousGridRows(gridRows) {
  // Ensure continuous gridRows with fixed time increment
  const allTimes = Array.from(gridRows)
    .map((label) => label.match(/time-(\d{4})/)?.[1])
    .filter(Boolean)
    .sort();
  const allGridRows = [];
  if (allTimes.length > 0) {
    let t = allTimes[0];
    const end = allTimes[allTimes.length - 1];
    while (t <= end) {
      allGridRows.push(`time-${t}`);
      t = nextTimeSlot(t);
    }
  }
  return allGridRows;
}

const DaySchedule = (props) => {
  const { day } = props;
  const keyDay = day['id'];
  const slots = day.items;
  const hasAllRoom = day.rooms.map((room) => room[0]).includes('_all_');
  const dayRooms = hasAllRoom
    ? day.rooms.filter((room) => room[0] !== '_all_')
    : day.rooms;
  const lenRooms = dayRooms.length;

  // Memoize grid calculations based on slots
  const { gridTemplateRows, gridTemplateColumns } = React.useMemo(() => {
    const { gridRows, gridColumns } = getGridRowsAndColumns(slots);
    const allGridRows = buildContinuousGridRows(gridRows);
    return {
      gridTemplateRows: [
        '[rooms] auto',
        ...allGridRows.map((row) => `[${row}] auto`),
      ].join('\n'),
      gridTemplateColumns:
        '[times] 5rem ' +
        gridColumns.map((column) => `[${column}] 1fr`).join(' ') +
        ' [room-all]',
    };
  }, [slots]);

  return (
    <div
      className={`tab-content ${keyDay} rooms rooms-${lenRooms}`}
      style={{
        gridTemplateRows,
        gridTemplateColumns,
      }}
    >
      <div
        className={`day-header`}
        style={{
          gridColumn: 'times',
          gridRow: 'rooms',
        }}
      >
        <div className="aside-indication">
          <div className="icon-type">
            <Icon name={roomSVG} />
          </div>
        </div>
      </div>
      {dayRooms.map((room, roomIndex) => {
        const roomId = room[0];
        const roomLabel = room[1] || roomId;
        return (
          <div
            className={`room-header ${roomId} room-${roomIndex}`}
            key={roomIndex}
            style={{ gridColumn: `room-${roomIndex + 1}`, gridRow: 'rooms' }}
          >
            <span className="room-label">{roomLabel}</span>
          </div>
        );
      })}
      {(() => {
        const timeSlots = {};
        const finalSlots = [];
        slots.forEach((slot, slotIndex) => {
          if (timeSlots[slot.start] === undefined) {
            const slot_category =
              slot.slot_category && slot.slot_category !== 'activity'
                ? slot.slot_category
                : slot['@type'];
            timeSlots[slot.start] = true;
            finalSlots.push({
              '@type': 'aside',
              start: slot.start,
              icon: iconDict[slot_category] || iconDict['Slot'],
              gridColumn: 'times',
              gridRow: slot.gridRow.split('/')[0].trim(),
            });
          }
          finalSlots.push(slot);
        });
        return finalSlots.flatMap((slot, slotIndex) => {
          if (slot['@type'] === 'aside') {
            const time = new Date(slot.start);
            return [
              <div
                className={`row-header`}
                key={slotIndex}
                style={{
                  gridColumn: 'times',
                  gridRow: slot.gridRow,
                }}
              >
                <div className="aside-indication">
                  <div className="icon-type">
                    <Icon name={slot.icon} />
                  </div>
                  <div className="time-indication">{formatHour(time)}</div>
                </div>
              </div>,
            ];
          } else {
            const isSession = slot?.['@type'] !== 'Slot';
            const Tile = slot && isSession ? SessionTile : SlotTile;
            const layout = planRoomAllSlot(slot, dayRooms);
            const tile = (
              <div
                className={`timeslot ${layout.roomClasses} rows-${slot.gridHeight}`}
                style={{
                  gridColumn: layout.gridColumn,
                  gridRow: slot.gridRow,
                }}
                data-start={slot.start}
                data-end={slot.end}
                key={slotIndex}
              >
                <div
                  className={`timeslot-container timeslot-${isSession ? 'session' : 'slot'}`}
                >
                  <Tile
                    item={slot}
                    shortDate
                    showRoom={true}
                    showDescription={true}
                    gridColumn={slot.gridColumn}
                    gridRow={slot.gridRow}
                  />
                </div>
              </div>
            );
            const blocked = layout.blockedColumns.map((column) => (
              <div
                className={`timeslot-blocked ${column}`}
                style={{ gridColumn: column, gridRow: slot.gridRow }}
                aria-hidden="true"
                key={`${slotIndex}-blocked-${column}`}
              />
            ));
            return [tile, ...blocked];
          }
        });
      })()}
    </div>
  );
};

export default DaySchedule;
